/* eslint-disable no-process-env */
import process from 'process';

import { bool, cleanEnv, num, str, url } from 'envalid';

export enum Env {
  Dev = 'dev',
  Prod = 'prod',
  Qa = 'qa',
  Stage = 'stage',
}

export const getAppEnv = () => {
  // Determine default port based on environment: use 9696 only when running locally
  const isCi =
    (process.env.CI || '').toString().toLowerCase() === 'true' ||
    process.env.CI === '1';
  const isEcs =
    Boolean(process.env.ECS) ||
    Boolean(process.env.ECS_CONTAINER_METADATA_URI) ||
    Boolean(process.env.ECS_CONTAINER_METADATA_URI_V4);
  const isNodeDev = process.env.NODE_ENV === 'development';
  const isEnvDev = (process.env.ENV || Env.Dev) === Env.Dev;
  const isLocalhostHint =
    (process.env.AUTH_COOKIE_DOMAIN || '') === 'localhost' ||
    (process.env.APP_URL || '').includes('localhost');
  const isLocal = !isCi && !isEcs && (isNodeDev || isEnvDev || isLocalhostHint);
  const defaultPort = isLocal ? 9696 : 9000;

  const env = cleanEnv(process.env, {
    AG_GRID_LICENSE: str({ default: '' }),
    ANALYZE: bool({ default: false }),
    APP_URL: str({ default: '' }),
    AUTH_COOKIE_DOMAIN: str({ default: 'localhost' }),
    DATADOG_CLIENT_TOKEN: str({ default: '' }),
    DD_ENV: str({ default: '' }),
    DD_SERVICE: str({ default: '' }),
    DD_TRACE_ENABLED: bool({ default: false }),
    DD_VERSION: str({ default: '' }),
    ENV: str({ choices: Object.values(Env), default: Env.Dev }),
    NEXT_PUBLIC_E2E_TEST: bool({ default: false }),
    NEXT_PUBLIC_HOTJAR_ID: num({ default: 0 }),
    NEXT_PUBLIC_HOTJAR_SV: num({ default: 0 }),
    NEXT_PUBLIC_OKTA_CLIENT_ID: str({ default: '' }),
    NEXT_PUBLIC_OKTA_ORG_URL: url({
      default: 'https://evenfinancialapp.oktapreview.com',
    }),
    NEXT_PUBLIC_PORTAL_API_URL: url({ default: '' }),
    NEXT_PUBLIC_RECURLY_PUBLIC_KEY: str({ default: '' }),
    NEXT_PUBLIC_TABLEAU_REPORTS_URL: url({
      default:
        'https://prod-useast-a.online.tableau.com/t/tableaueven/views/BenchmarkReportsProduction-3_0/LenderBenchmarking',
    }),
    NODE_ENV: str({ default: 'dev' }),
    PORT: num({ default: defaultPort }),
    PORTAL_API_URL: str({ default: '' }),
    PP_EVEN_BASE_URL: url({
      default: 'https://dev.consumer.evenfinancial.com',
    }),
    PP_FIONA_BASE_URL: url({
      default: 'https://dev.fiona.com/partner',
    }),
    PP_LEAPLIFE_BASE_URL: url({
      default: 'https://dev.leaplife.com/partner',
    }),
    RECURLY_PUBLIC_KEY: str({ default: '' }),
    SENTRY_DSN: str({ default: '' }),
    SENTRY_ENV: str({ default: 'development' }),
    TABLEAU_BASE_URL: url({ default: 'https://tableau.evenfinancial.com/' }),
    TABLEAU_FOLDER: str({ default: 'BenchmarkReportsProduction-V11_1' }),
    docker_version: str({ default: '' }),
  });

  const PARTNER_PAGE_BASE_URLS = {
    evenBaseUrl: env.PP_EVEN_BASE_URL,
    fionaBaseUrl: env.PP_FIONA_BASE_URL,
    leaplifeBaseUrl: env.PP_LEAPLIFE_BASE_URL,
  };

  return {
    ...env,
    IS_PRODUCTION_ENV: env.ENV === 'prod',
    IS_QA_ENV: env.ENV === 'qa',
    IS_SERVER: typeof (globalThis as any).window === 'undefined',
    IS_STAGING_ENV: env.ENV === 'stage',
    PARTNER_PAGE_BASE_URLS,
  };
};

export type Environment = ReturnType<typeof getAppEnv>;
export type AppEnv = ReturnType<typeof getAppEnv>;

export const getRuntimeConfig = (): Environment => {
  return getAppEnv();
};
